/**
 * Agents › Reports — written analyses in slide format.
 *
 * The library is the Deliverables view pre-filtered to presentations. "Create
 * a report" opens the gallery: Start from scratch (write, upload material, or a
 * strategy call with Lykke — she builds it), Import (PowerPoint, PDF, pictures),
 * then every template (the info button shows all its pages). A template asks
 * what the report is about and Lykke builds it. `?edit=<id>` opens a deck.
 */
import { useEffect, useRef, useState } from 'react';
import { NumberField } from '@/cowork/shared/NumberField';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Plus, Loader2, X, ArrowLeft, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import { VisibilityControl, type VisibilityValue } from '@/cowork/omniad/components/VisibilityControl';
import { toast } from 'sonner';
import { ReportsLibrary } from './ReportsLibrary';
import { DeckStart } from './DeckStart';
import { coworkApi, type Bundle, type BundleItem, type DeckSettings } from '@/cowork/omniad/api/coworkApi';
import { useActiveBrand } from '@/cowork/useActiveBrand';
import { useAuth } from '@/contexts/AuthContext';
import { useCampaigns } from '@/hooks/useCampaigns';
import { ReportEditor } from './ReportEditor';
import { type ReportTemplate } from './templates';
import { TemplateGallery } from './create/TemplateGallery';
import { StartFromScratch, type ScratchStart } from './create/StartFromScratch';
import { FileDrop } from './import/ImportPagesDialog';
import type { ImportResult } from './import/importSlides';
import { ProposalBuilder, type ProposalSession } from '@/cowork/proposals/ProposalBuilder';
import { useIsStaff, useStaffState } from '@/cowork/useIsStaff';
import { CapMeter } from '@/cowork/launch/CapMeter';
import { useHostLocationSync } from '@/cowork/hostLocation';

export function ReportsPage() {
  const [params, setParams] = useSearchParams();
  // In the back office frame: the host's address follows the open deck (?edit=<id>), so the link can be shared.
  useHostLocationSync(params);
  const editId = params.get('edit');
  const { brandId } = useActiveBrand();
  // "Create a deck" opens the gallery: Start from scratch, Import, then the templates.
  const [picking, setPicking] = useState(false);
  const [proposal, setProposal] = useState(false);
  const qc = useQueryClient();
  const refreshLibrary = () => { void qc.invalidateQueries({ queryKey: ['cowork', 'reports', 'library'] }); };
  useEffect(() => {
    const tk = params.get('tiktok');
    if (tk) { if (tk === 'connected') toast.success(`TikTok Business connected${params.get('advertisers') ? ` (${params.get('advertisers')} advertiser account${params.get('advertisers') === '1' ? '' : 's'})` : ''}: Creative Center trends will show up in proposals.`); else toast.error(`TikTok connect failed: ${params.get('reason') || 'unknown'}`); const next = new URLSearchParams(params); next.delete('tiktok'); next.delete('advertisers'); next.delete('reason'); setParams(next, { replace: true }); }
  }, [params, setParams]);
  const [proposalPlanId, setProposalPlanId] = useState<string | undefined>(undefined);
  // Complete proposals are built from media plans, which live in the back office: staff only (null = still asking).
  const { staff, failed: staffUnknown } = useStaffState();
  useEffect(() => { if (params.get('new') === 'proposal' && staff !== null) { if (staff) { setProposalPlanId(params.get('plan') || undefined); setProposal(true); } const next = new URLSearchParams(params); next.delete('new'); next.delete('plan'); setParams(next, { replace: true }); } }, [params, setParams, staff]);

  // Deep link (Lykke the guide, or any link): /agents/reports?new=deck&ask=… makes a deck and builds it from the ask,
  // exactly like the box on this page does.
  const startedFromLink = useRef(false);
  useEffect(() => {
    const ask = params.get('ask');
    if (params.get('new') !== 'deck' || !ask || params.get('edit') || startedFromLink.current) return;
    startedFromLink.current = true;
    void coworkApi.presentations.create({ title: 'Untitled deck', brandId, brandChosen: true })
      .then((created) => { const next = new URLSearchParams(params); next.delete('new'); next.set('edit', created.id); next.set('ask', ask); setParams(next, { replace: true }); })
      .catch((e) => { startedFromLink.current = false; toast.error((e as Error).message); });
  }, [params, setParams, brandId]);

  /** `ask` opens the deck chat with that request (a house-script template), the same way the ?ask= deep link does. */
  const openEditor = (id: string, ask?: string) => {
    const next = new URLSearchParams(params);
    next.set('edit', id);
    if (ask) next.set('ask', ask);
    setParams(next);
  };
  const closeEditor = () => {
    const next = new URLSearchParams(params);
    next.delete('edit');
    setParams(next);
    refreshLibrary();
  };

  // An in-progress Complete proposal reopens the builder where it left off; everything else opens the editor.
  // A deck that is not in this workspace answers 404 at once: no retries (they only kept the spinner going).
  const opened = useQuery({ queryKey: ['cowork', 'reports', 'open', editId], queryFn: () => coworkApi.presentations.get(editId as string), enabled: !!editId, staleTime: 0, retry: (n, e) => n < 2 && !/not found|workspace/i.test((e as Error).message) });
  if (editId) {
    if (opened.isError) return (
      <div className="mx-auto max-w-lg py-24 text-center">
        <div className="text-[15px] font-semibold text-[#101828]">This deck can't be opened here</div>
        <p className="mt-2 text-[13px] text-[#475467]">{(opened.error as Error).message}</p>
        <button onClick={closeEditor} className="mt-5 rounded-lg border border-[#d0d5dd] bg-white px-3 py-1.5 text-[13px] font-medium text-[#344054] hover:bg-[#f9fafb]">Back to decks</button>
      </div>
    );
    if (opened.isPending) return <div className="flex justify-center py-24"><Loader2 className="h-5 w-5 animate-spin text-[#667085]" /></div>;
    const g = opened.data?.settings?.generation;
    const sess = opened.data?.settings?.proposal as ProposalSession | undefined;
    if (g?.kind === 'proposal' && g.status === 'running' && sess?.intent && staff === null && !staffUnknown) return <div className="flex justify-center py-24"><Loader2 className="h-5 w-5 animate-spin text-[#667085]" /></div>;
    if (g?.kind === 'proposal' && g.status === 'running' && sess?.intent && staff) {
      // Built: re-read the deck (its generation is now 'done') so this branch hands over to the editor.
      return <ProposalBuilder key={editId} draftId={editId} resume={sess} brandId={sess.brandId || brandId} onClose={closeEditor} onCreated={(id) => { refreshLibrary(); void qc.invalidateQueries({ queryKey: ['cowork', 'reports', 'open', id] }); if (id !== editId) { const next = new URLSearchParams(params); next.set('edit', id); setParams(next, { replace: true }); } }} />;
    }
    return <ReportEditor key={editId} presentationId={editId} onBack={closeEditor} />;
  }

  return (
    <div className="relative">
      <ReportsLibrary
        onOpen={openEditor}
        lead={<DeckStart onStart={(id, ask) => { const next = new URLSearchParams(params); next.set('edit', id); next.set('ask', ask); setParams(next); }} />}
        action={(
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* This month's use for a client team ("12 of 30 decks this month"); nothing for staff. */}
            <CapMeter studio="deck" unit="decks" />
            <CapMeter studio="deck" unit="edits" />
            <button onClick={() => setPicking(true)} className="flex items-center gap-1.5 rounded-lg border border-[#d0d5dd] bg-white px-3 py-1.5 text-[12.5px] font-medium text-[#344054] hover:bg-[#f9fafb]" title="Start from a template, a file, or an empty deck">
              <Plus className="h-3.5 w-3.5" /> Template or file
            </button>
          </div>
        )}
      />
      {picking && <CreateReportModal brandId={brandId} onClose={() => setPicking(false)} onCreated={(id, ask) => { setPicking(false); refreshLibrary(); openEditor(id, ask); }} onBuilder={() => { setPicking(false); setProposal(true); }} />}
      {proposal && staff && <ProposalBuilder brandId={brandId} planId={proposalPlanId} onClose={() => { setProposal(false); refreshLibrary(); }} onCreated={(id) => { setProposal(false); refreshLibrary(); openEditor(id); }} />}
    </div>
  );
}

// ─── Two-step create modal ──────────────────────────────────────────────────

const inputCls = 'w-full rounded-lg border border-[#d0d5dd] px-3 py-2 text-[13px] text-[#101828] focus:border-[#be43ff] focus:outline-none';
const labelCls = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[#667085]';

function CreateReportModal({ brandId: initialBrandId, onClose, onCreated, onBuilder }: { brandId?: string; onClose: () => void; onCreated: (id: string, ask?: string) => void; onBuilder: () => void }) {
  // Many teams hold several brands (Maxim and Möller share one team), so the deck's brand is chosen here, on
  // screen, not inherited silently from whatever was picked last. Changing it also moves the sticky choice.
  const { brands, selectBrand } = useActiveBrand();
  const [brandId, setBrandId] = useState<string | undefined>(initialBrandId);
  // Picked here by hand: a brand named in the brief does not override it (backend brandChosen).
  const [brandChosen, setBrandChosen] = useState(false);
  const chooseBrand = (id: string) => { setBrandId(id); selectBrand(id); setBrandChosen(true); };
  const [tpl, setTpl] = useState<ReportTemplate | null>(null);
  const [busy, setBusy] = useState<'empty' | 'kai' | null>(null);
  const staff = useIsStaff();
  // The gallery, or one of its two first cards: Start from scratch / Import.
  const [step, setStep] = useState<'gallery' | 'scratch' | 'import'>('gallery');
  const [brief, setBrief] = useState('');
  const [pages, setPages] = useState<number>(8);
  const [campaign, setCampaign] = useState<{ id: string; name: string } | null>(null);
  // A bundle of proposals / media plans: the source the programme templates are built from; optional material for the others.
  const [bundle, setBundle] = useState<Bundle | null>(null);
  const [fetchComments, setFetchComments] = useState(false);
  const [vis, setVis] = useState<VisibilityValue>({ visibility: 'team', allowedUserIds: [] });
  // AI concept visuals: UGC-style by default; "advanced" opens the user's own ideas + format.
  const [aiContent, setAiContent] = useState(false);
  const [aiAdvanced, setAiAdvanced] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiCount, setAiCount] = useState(4);
  const [aiAspect, setAiAspect] = useState<'9:16' | '4:5' | '1:1' | '16:9'>('9:16');
  const [aiStyle, setAiStyle] = useState<'ugc' | 'product' | 'lifestyle' | 'editorial'>('ugc');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pick = (t: ReportTemplate) => {
    if (t.builder === 'proposal') { onBuilder(); return; }
    setTpl(t);
    setPages(t.brief?.pagesDefault ?? Math.max(t.pages, 4));
    setBrief('');
    setCampaign(null);
    setBundle(null);
    setFetchComments(!!t.brief?.comments);
    setAiContent(false); setAiAdvanced(false); setAiPrompt('');
  };

  const startEmpty = async () => {
    if (!tpl) return;
    setBusy('empty');
    try {
      const created = await coworkApi.presentations.create({
        title: tpl.id === 'blank' ? 'Untitled report' : tpl.name,
        brandId,
        brandChosen,
        slides: tpl.slides.map((s) => ({ ...s })),
        templateId: tpl.id,
        templateName: tpl.name,
        visibility: vis.visibility,
        allowedUserIds: vis.allowedUserIds,
      });
      onCreated(created.id);
    } catch (e) {
      toast.error(`Could not create the report: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  /** A file becomes an editable report: PowerPoint (editable pages), PDF (a picture per page), pictures, or our deck JSON. */
  const createFromImport = async (r: ImportResult) => {
    try {
      const created = await coworkApi.presentations.create({ title: r.title.trim() || 'Imported report', brandId, brandChosen, slides: r.slides, templateName: 'Imported', visibility: vis.visibility, allowedUserIds: vis.allowedUserIds });
      if (r.settings && typeof r.settings === 'object') await coworkApi.presentations.update(created.id, { settings: r.settings as DeckSettings });
      toast.success(`Imported ${r.slides.length} pages${r.skipped.length ? ` (left out: ${r.skipped.join(', ')})` : ''}`);
      onCreated(created.id);
    } catch (e) { toast.error(`Could not import: ${(e as Error).message}`); }
  };

  /** From scratch: a blank deck that carries the confirmed brief; the editor opens Lykke, she plans the pages and (Build it) writes them all. */
  const startScratch = async (st: ScratchStart) => {
    const created = await coworkApi.presentations.create({ title: st.title, brandId, brandChosen, templateId: 'blank', templateName: 'Build with Lykke', pendingBrief: st.brief, pendingAutoBuild: st.autoBuild, visibility: st.visibility.visibility, allowedUserIds: st.visibility.allowedUserIds });
    onCreated(created.id);
  };

  /** A house-script template: an empty deck in the chosen brand, then the deck chat builds it from the script. */
  const buildFromScript = async () => {
    if (!tpl?.script) return;
    if (!brief.trim()) { toast.message('Name the prospect first: the deck is built for them.'); return; }
    setBusy('kai');
    try {
      const created = await coworkApi.presentations.create({ title: tpl.name, brandId, brandChosen, templateId: tpl.id, templateName: tpl.name, visibility: vis.visibility, allowedUserIds: vis.allowedUserIds });
      onCreated(created.id, `${tpl.script.ask} ${brief.trim()}`);
    } catch (e) {
      toast.error(`Could not start the deck: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const buildWithKai = async () => {
    if (!tpl) return;
    if (tpl.script) return buildFromScript();
    const needsCampaign = tpl.brief?.kind === 'campaign'; const needsBundle = tpl.brief?.kind === 'bundle';
    if (needsBundle && !bundle) { toast.message('Pick a bundle: this template is built from its plans and proposals.'); return; }
    if (needsCampaign && !campaign && !brief.trim() && !bundle) { toast.message('Pick a campaign or describe what to cover.'); return; }
    if (!needsCampaign && !needsBundle && !brief.trim() && !bundle) { toast.message('Tell Lykke what the report is about.'); return; }
    setBusy('kai');
    try {
      const created = await coworkApi.presentations.generate({
        brandId,
        brandChosen,
        brief: brief.trim(),
        pages,
        campaign: campaign ?? undefined,
        templateId: tpl.id,
        templateName: tpl.id === 'blank' ? undefined : tpl.name,
        outline: tpl.id === 'blank' ? undefined : tpl.slides,
        bundleId: bundle?.id,
        fetchComments: !!(tpl.brief?.comments && campaign && fetchComments),
        flexiblePages: !!tpl.brief?.flexiblePages,
        visibility: vis.visibility,
        allowedUserIds: vis.allowedUserIds,
        aiContent: aiContent ? { enabled: true, count: aiCount, aspect: aiAspect, style: aiStyle, prompt: aiPrompt.trim() || undefined } : undefined,
      });
      toast.success('Lykke is building your report: pages will appear as soon as it’s done.');
      onCreated(created.id);
    } catch (e) {
      toast.error(`Could not start the report: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose} className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 p-6">
      <div onClick={(e) => e.stopPropagation()} className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#eaecf0] px-6 py-4">
          <div className="flex items-center gap-3">
            {(tpl || step !== 'gallery') && (
              <button onClick={() => (tpl ? setTpl(null) : setStep('gallery'))} className="rounded-md p-1 text-[#667085] hover:bg-[#f2f4f7]" aria-label="Back"><ArrowLeft className="h-4 w-4" /></button>
            )}
            <div>
              <div className="text-[16px] font-semibold text-[#101828]">{tpl ? tpl.name : step === 'scratch' ? 'Start from scratch' : step === 'import' ? 'Import' : 'Create a report'}</div>
              <div className="text-[12px] text-[#667085]">{tpl ? (tpl.brief?.label ?? 'Tell Lykke what this report is about.') : step === 'scratch' ? 'Write it, upload your material, or talk it through with Lykke, then she builds it.' : step === 'import' ? 'PowerPoint pages stay editable; PDF pages come in as pictures.' : 'Start from scratch, import a file, or pick a template. The i on a template shows every page.'}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {brands.length > 1 && (
              <label className="flex items-center gap-2 text-[12px] text-[#667085]">
                Brand
                <select value={brandId || ''} onChange={(e) => chooseBrand(e.target.value)} className="rounded-md border border-[#d0d5dd] bg-white px-2 py-1 text-[13px] font-medium text-[#101828]" aria-label="Brand for this deck">
                  {!brandId && <option value="">Pick a brand</option>}
                  {brands.map((b) => <option key={b.brandId} value={b.brandId}>{b.brandName}</option>)}
                </select>
              </label>
            )}
            <button onClick={onClose} className="rounded-md p-1 text-[#667085] hover:bg-[#f2f4f7]" aria-label="Close"><X className="h-4 w-4" /></button>
          </div>
        </div>

        {!tpl && step === 'scratch' ? (
          <StartFromScratch brandId={brandId} onStart={startScratch} />
        ) : !tpl && step === 'import' ? (
          <div className="flex-1 space-y-4 overflow-y-auto p-6">
            <FileDrop onDone={(_from, r) => createFromImport(r)} />
            <div className="flex items-center justify-between gap-3 rounded-lg border border-[#eaecf0] px-3 py-2">
              <span className="text-[12px] text-[#344054]"><span className="font-semibold text-[#101828]">Who can see it</span>, you can change this later</span>
              <VisibilityControl value={vis} onChange={setVis} size="sm" />
            </div>
          </div>
        ) : !tpl ? (
          <div className="flex-1 overflow-y-auto p-6">
            <TemplateGallery onScratch={async () => { try { const created = await coworkApi.presentations.create({ title: 'Untitled deck', brandId, brandChosen, visibility: vis.visibility, allowedUserIds: vis.allowedUserIds }); onCreated(created.id); } catch (e) { toast.error((e as Error).message); } }} onImport={() => setStep('import')} onPick={pick} />
          </div>
        ) : (
          <div className="grid flex-1 grid-cols-[1fr_18rem] gap-6 overflow-y-auto p-6">
            <div className="space-y-4">
              {tpl.brief?.kind === 'campaign' && (
                <div>
                  <label className={labelCls}>Campaign</label>
                  <CampaignSelect value={campaign} onChange={setCampaign} />
                  {tpl.brief?.comments && (
                    <label className="mt-2 flex items-start gap-2 text-[12px] text-[#344054]">
                      <input type="checkbox" checked={fetchComments} onChange={(e) => setFetchComments(e.target.checked)} className="mt-0.5 accent-[#be43ff]" />
                      <span>Fetch audience comments from the campaign's posts first (Instagram / TikTok, top 10 posts). Adds a minute or two; powers the “Audience response” page.</span>
                    </label>
                  )}
                </div>
              )}
              {!tpl.script && (staff || tpl.brief?.kind === 'bundle') && <div>
                <label className={labelCls}>{tpl.brief?.kind === 'bundle' ? 'Bundle' : 'Bundle (optional)'}</label>
                <BundleSelect brandId={brandId} value={bundle} onChange={setBundle} />
                <div className="mt-1 text-[11px] text-[#98a2b3]">{tpl.brief?.kind === 'bundle' ? 'Several proposals and/or media plans for one client, read as one programme: the numbers come from them.' : 'Give Lykke a bundle of proposals / media plans and every number in the deck comes from them.'}</div>
              </div>}
              <div>
                <label className={labelCls}>{tpl.script ? 'Who it\u2019s for' : tpl.brief?.kind === 'campaign' || tpl.brief?.kind === 'bundle' ? 'Anything to emphasise? (optional)' : 'Brief'}</label>
                <textarea
                  autoFocus
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  rows={tpl.id === 'blank' ? 14 : 9}
                  placeholder={tpl.brief?.placeholder ?? 'Describe the report…'}
                  className={`${inputCls} resize-y leading-relaxed`}
                />
                <div className="mt-1 text-[11px] text-[#98a2b3]">Lykke plans the pages, pulls live campaign data and Content Hub media where it fits, and writes every page. You can edit everything after.</div>
              </div>

              <div className="flex items-center justify-between gap-3 rounded-lg border border-[#eaecf0] px-3 py-2">
                <span className="text-[12px] text-[#344054]"><span className="font-semibold text-[#101828]">Who can see it</span>, you can change this later</span>
                <VisibilityControl value={vis} onChange={setVis} size="sm" />
              </div>
              {!tpl.script && <div className="rounded-lg border border-[#eaecf0] p-3">
                <label className="flex items-start gap-2 text-[12px] text-[#344054]">
                  <input type="checkbox" checked={aiContent} onChange={(e) => setAiContent(e.target.checked)} className="mt-0.5 accent-[#be43ff]" />
                  <span><span className="font-semibold text-[#101828]">Generate AI content</span>, Lykke briefs the image model from your brief and the brand and makes UGC-style concept visuals (phone-shot, real people, natural light) for the cover, strategy and creative-direction pages. Never shown as creator posts.</span>
                </label>
                {aiContent && (
                  <div className="mt-2 pl-6">
                    <button type="button" onClick={() => setAiAdvanced((v) => !v)} className="flex items-center gap-1 text-[12px] font-semibold text-[#7c3aed] hover:underline">
                      {aiAdvanced ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Advanced content gen options
                    </button>
                    {aiAdvanced && (
                      <div className="mt-2 space-y-3">
                        <div>
                          <label className={labelCls}>Your content ideas (optional)</label>
                          <textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={4} placeholder="e.g. Trail runners at dawn above Chamonix, muddy shoes in close-up, a creator filming a selfie-style review at the finish line. Anything you write here overrides the UGC default." className={`${inputCls} resize-y leading-relaxed`} />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className={labelCls}>Style</label>
                            <select value={aiStyle} onChange={(e) => setAiStyle(e.target.value as typeof aiStyle)} className={inputCls}>
                              <option value="ugc">UGC (default)</option>
                              <option value="lifestyle">Lifestyle</option>
                              <option value="product">Product-led</option>
                              <option value="editorial">Editorial</option>
                            </select>
                          </div>
                          <div>
                            <label className={labelCls}>Format</label>
                            <select value={aiAspect} onChange={(e) => setAiAspect(e.target.value as typeof aiAspect)} className={inputCls}>
                              <option value="9:16">Vertical 9:16</option>
                              <option value="4:5">Portrait 4:5</option>
                              <option value="1:1">Square 1:1</option>
                              <option value="16:9">Wide 16:9</option>
                            </select>
                          </div>
                          <div>
                            <label className={labelCls}>How many</label>
                            <NumberField min={1} max={8} value={aiCount} onChange={(n) => setAiCount(n ?? 1)} className={inputCls} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>}
            </div>

            <div className="space-y-4">
              {!tpl.script && <div>
                <label className={labelCls}>How many pages</label>
                <NumberField min={2} max={40} value={pages} onChange={(n) => setPages(n ?? 2)} className={inputCls} />
                <div className="mt-1 text-[11px] text-[#98a2b3]">{tpl.brief?.flexiblePages ? 'A target: one page per creator and per channel can add more.' : 'Lykke keeps to this count.'}</div>
              </div>}
              <div className="rounded-lg border border-[#eaecf0] p-3 text-[11px] text-[#667085]">
                <div className="mb-1 font-semibold text-[#101828]">Template skeleton</div>
                <ol className="list-decimal space-y-0.5 pl-4">
                  {tpl.slides.map((s, i) => <li key={i}>{String((s as any).title || (s as any).type)}</li>)}
                </ol>
                <div className="mt-2">{tpl.script ? 'The story Lykke follows, in the brand\u2019s own look. Pages without data yet are named in her reply.' : 'Used as guidance: Lykke adapts it to your brief.'}</div>
              </div>
              <button onClick={buildWithKai} disabled={!!busy} className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#be43ff] px-3.5 py-2.5 text-[13px] font-semibold text-white hover:bg-[#a835e0] disabled:opacity-50">
                {busy === 'kai' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Build with Lykke
              </button>
              <button onClick={startEmpty} disabled={!!busy} className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#d0d5dd] px-3.5 py-2 text-[13px] font-medium text-[#344054] hover:bg-[#f9fafb] disabled:opacity-50">
                {busy === 'empty' ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Start with the empty template
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Pick a bundle, or make one on the spot from the team's proposals and media plans. */
function BundleSelect({ brandId, value, onChange }: { brandId?: string; value: Bundle | null; onChange: (b: Bundle | null) => void }) {
  const qc = useQueryClient();
  const bundles = useQuery({ queryKey: ['cowork', 'bundles', brandId || ''], queryFn: () => coworkApi.bundles.list(brandId), staleTime: 60_000 });
  const [making, setMaking] = useState(false);
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<BundleItem[]>([]);
  const [busy, setBusy] = useState(false);
  const sources = useQuery({ queryKey: ['cowork', 'bundle-sources', brandId || ''], queryFn: () => coworkApi.bundles.sources(brandId), enabled: making, staleTime: 60_000 });
  const has = (id: string) => picked.some((i) => i.id === id);
  const toggle = (item: BundleItem) => setPicked((cur) => (cur.some((i) => i.id === item.id) ? cur.filter((i) => i.id !== item.id) : [...cur, item]));
  const create = async () => {
    if (!picked.length) { toast.message('Tick at least one proposal or media plan.'); return; }
    setBusy(true);
    try { const r = await coworkApi.bundles.create({ name: name.trim() || `${picked[0].title} +${picked.length - 1}`, brandId: brandId || null, items: picked }); await qc.invalidateQueries({ queryKey: ['cowork', 'bundles'] }); onChange(r.bundle); setMaking(false); setPicked([]); setName(''); }
    catch (e) { toast.error((e as Error).message); } finally { setBusy(false); }
  };
  const list = bundles.data?.bundles || [];
  const money = (n: number, cur?: string) => `${Math.round(n).toLocaleString('sv-SE')} ${cur || ''}`.trim();
  return (
    <div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <select value={value?.id ?? ''} onChange={(e) => onChange(list.find((b) => b.id === e.target.value) || null)} className={`${inputCls} appearance-none pr-8`}>
            <option value="">{bundles.isLoading ? 'Loading bundles…' : list.length ? 'Pick a bundle' : 'No bundles yet: make one'}</option>
            {list.map((b) => <option key={b.id} value={b.id}>{b.name} · {b.items.length} item{b.items.length === 1 ? '' : 's'}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#98a2b3]" />
        </div>
        <button type="button" onClick={() => setMaking((v) => !v)} className="shrink-0 rounded-lg border border-[#d0d5dd] px-3 py-2 text-[12px] font-medium text-[#344054] hover:bg-[#f9fafb]">{making ? 'Cancel' : '+ New bundle'}</button>
      </div>
      {value && <div className="mt-1.5 flex flex-wrap gap-1">{value.items.map((i) => <span key={i.id} className="rounded-full bg-[#f2f4f7] px-2 py-0.5 text-[11px] text-[#475467]">{i.kind === 'proposal' ? '📄' : '📊'} {i.title}</span>)}</div>}
      {making && (
        <div className="mt-2 rounded-lg border border-[#eaecf0] p-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Bundle name, e.g. Rapid 2027: four markets" className={inputCls} />
          {sources.isLoading && <div className="py-3 text-[12px] text-[#98a2b3]">Loading proposals and media plans…</div>}
          {sources.data && (
            <div className="mt-2 grid max-h-56 gap-3 overflow-y-auto md:grid-cols-2">
              <div>
                <div className={labelCls}>Proposals</div>
                {!sources.data.proposals.length && <div className="text-[12px] text-[#98a2b3]">No proposals yet (Reports → Complete proposal).</div>}
                {sources.data.proposals.map((p) => <label key={p.presentationId} className="flex cursor-pointer items-start gap-2 py-1 text-[12px] text-[#344054]"><input type="checkbox" checked={has(p.presentationId)} onChange={() => toggle({ kind: 'proposal', id: p.presentationId, title: p.campaignName || p.title })} className="mt-0.5 accent-[#be43ff]" /><span><span className="font-medium text-[#101828]">{p.campaignName || p.title}</span><span className="text-[#98a2b3]"> · {[p.market, p.budget ? money(p.budget, p.currency) : null, p.complete ? 'complete' : 'in progress'].filter(Boolean).join(' · ')}</span></span></label>)}
              </div>
              <div>
                <div className={labelCls}>Media plans</div>
                {!sources.data.plans.length && <div className="text-[12px] text-[#98a2b3]">No media plans for this brand.</div>}
                {sources.data.plans.map((p) => <label key={p.id} className="flex cursor-pointer items-start gap-2 py-1 text-[12px] text-[#344054]"><input type="checkbox" checked={has(p.id)} onChange={() => toggle({ kind: 'plan', id: p.id, title: p.name })} className="mt-0.5 accent-[#be43ff]" /><span><span className="font-medium text-[#101828]">{p.name}</span><span className="text-[#98a2b3]"> · {[p.market, money(p.totalBudget, p.currency)].filter(Boolean).join(' · ')}</span></span></label>)}
              </div>
            </div>
          )}
          <div className="mt-2 flex items-center justify-between"><span className="text-[11px] text-[#98a2b3]">{picked.length} picked</span><button type="button" onClick={create} disabled={busy} className="rounded-lg bg-[#be43ff] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[#a835e0] disabled:opacity-50">{busy ? 'Saving…' : 'Save bundle'}</button></div>
        </div>
      )}
    </div>
  );
}

function CampaignSelect({ value, onChange }: { value: { id: string; name: string } | null; onChange: (c: { id: string; name: string } | null) => void }) {
  const { user } = useAuth();
  const teamId = user?.team?.team_id ?? '';
  const { data, isLoading, error } = useCampaigns({ teamScope: teamId, pageSize: 50, filters: { sortBy: 'newest' } });
  const campaigns = data?.campaigns ?? [];
  return (
    <div className="relative">
      <select
        value={value?.id ?? ''}
        onChange={(e) => { const c = campaigns.find((x) => x.campaign_id === e.target.value); onChange(c ? { id: c.campaign_id, name: c.name } : null); }}
        className={`${inputCls} appearance-none pr-8`}
      >
        <option value="">{isLoading ? 'Loading campaigns…' : error ? 'Could not load campaigns' : campaigns.length ? 'Pick a campaign' : 'No campaigns found'}</option>
        {campaigns.map((c) => (
          <option key={c.campaign_id} value={c.campaign_id}>{c.name}{c.status ? ` · ${c.status}` : ''}</option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#98a2b3]" />
    </div>
  );
}
